import { useMemo } from "react";
import { defaultCities } from "../../app/common/config";
import { getWindow } from "../../helpers/getWindow";
import {
    useCountry,
    useIsPersistingForbidden,
} from "../ApplicationState/hooks";
import { sendError } from "../ErrorBoundary/helpers";
import config from "./config";
import type { FavoriteType, LocalStorageFavoriteItem } from "./types";

export const useFavorites = (): Array<LocalStorageFavoriteItem> => {
    const window = getWindow();
    const country = useCountry();
    const isPersistingForbidden = useIsPersistingForbidden();
    const defaultCity = defaultCities[country];
    const localStorageKey = config.localStorageKey[country];

    return useMemo(() => {
        if (isPersistingForbidden && defaultCity !== null) {
            return [defaultCity];
        }

        try {
            const favoriteStore = window?.localStorage.getItem(localStorageKey);
            const favoriteStoreObj = JSON.parse(
                favoriteStore ?? '{"favorites": []}'
            ) as { favorites: Array<FavoriteType> };

            if (favoriteStoreObj.favorites.length > 0) {
                return favoriteStoreObj.favorites
                    .filter(
                        (item: FavoriteType) =>
                            Object.keys(item.forceOverwrite ?? []).length === 0
                    )
                    .map((item: FavoriteType) => ({
                        code: item.cityCode,
                        name: item.cityName,
                        slug: item.seoString,
                        countryCode: item.countryCode,
                    }));
            } else if (defaultCity !== null) {
                return [defaultCity];
            }
        } catch (e) {
            sendError(e);
        }

        return [];
    }, [
        window?.localStorage,
        defaultCity,
        isPersistingForbidden,
        localStorageKey,
    ]);
};
